import { AutomatedNameUpdateStatus } from "@clipboard-health/contract-worker-app-bff";
import { BottomSheet, type BottomSheetProps, Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, DialogContent, Stack } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { invalidateWorkerRequirements } from "../../Documents/api/invalidateWorkerRequirements";
import { workerRequirementsBaseQueryKey } from "../../Documents/api/useGetWorkerRequirements";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";
import { useUpdateAutomatedNameUpdate } from "./useUpdateAutomatedNameUpdate";

type PersonalIdVerificationApprovedWithNameUpdateBottomSheetContentProps = {
  previousName: string;
  newName: string;
  automatedNameUpdateId: string;
  analytics: OnboardingPersonalIdVerificationAnalytics;
} & Pick<BottomSheetProps, "canDismiss" | "modalState" | "paperSx">;

export function PersonalIdVerificationApprovedWithNameUpdateBottomSheet({
  canDismiss,
  modalState,
  paperSx,
  previousName,
  newName,
  automatedNameUpdateId,
  analytics,
}: PersonalIdVerificationApprovedWithNameUpdateBottomSheetContentProps) {
  const { showErrorToast } = useToast();

  useLogEffect(
    APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_APPROVED_WITH_NAME_UPDATE_BOTTOM_SHEET_VIEWED,
    { ...analytics }
  );

  const queryClient = useQueryClient();
  // Track which button was clicked to show loading only on that button while disabling both
  // This allows us to use a single mutation instance while maintaining separate loading states per button
  // Also ensures loading continues to show on the clicked button during refetch after mutation completes
  const lastActionRef = useRef<"accept" | "flag" | undefined>(undefined);
  const { mutate: updateAutomatedNameUpdate, isLoading: isUpdating } = useUpdateAutomatedNameUpdate(
    {
      onError: () => {
        showErrorToast(
          "Sorry, something went wrong while acknowledging the name update. Please try again."
        );
        lastActionRef.current = undefined;
      },
    }
  );
  // Required to show loading state after invalidating worker requirements
  const isFetchingWorkerRequirements = useIsFetching(workerRequirementsBaseQueryKey) > 0;
  const isFetchingWorker = useIsFetching([GET_WORKER_QUERY_KEY]) > 0;

  const isLoading = isUpdating || isFetchingWorkerRequirements || isFetchingWorker;

  const items = [
    {
      label: "Name you entered",
      value: {
        text: previousName,
        color: (theme: Theme) => theme.palette.intent?.disabled.text,
      },
    },
    {
      label: "Updated to match ID",
      value: {
        text: newName,
        color: (theme: Theme) => theme.palette.intent?.success.text,
      },
    },
  ];

  async function onSuccess() {
    await Promise.all([
      invalidateWorkerRequirements(queryClient),
      invalidateGetWorkerQuery(queryClient),
    ]);
    lastActionRef.current = undefined;
  }

  return (
    <BottomSheet
      canDismiss={canDismiss}
      modalState={modalState}
      paperSx={paperSx}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            isLoading={isLoading && lastActionRef.current === "accept"}
            disabled={isLoading && isDefined(lastActionRef.current)}
            size="large"
            onClick={() => {
              lastActionRef.current = "accept";
              const status = AutomatedNameUpdateStatus.ACCEPTED;
              logEvent(
                APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_ACKNOWLEDGE_NAME_UPDATE_BUTTON_TAPPED,
                { ...analytics, status }
              );
              updateAutomatedNameUpdate(
                { automatedNameUpdateId, attributes: { status } },
                { onSuccess }
              );
            }}
          >
            Confirm
          </LoadingButton>
          <LoadingButton
            fullWidth
            isLoading={isLoading && lastActionRef.current === "flag"}
            disabled={isLoading && isDefined(lastActionRef.current)}
            size="large"
            variant="outlined"
            onClick={() => {
              lastActionRef.current = "flag";
              const status = AutomatedNameUpdateStatus.FLAGGED;
              logEvent(
                APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_ACKNOWLEDGE_NAME_UPDATE_BUTTON_TAPPED,
                { ...analytics, status }
              );
              updateAutomatedNameUpdate(
                {
                  automatedNameUpdateId,
                  attributes: { status: AutomatedNameUpdateStatus.FLAGGED },
                },
                { onSuccess }
              );
            }}
          >
            Something wrong with the name from my ID
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack gap={7} textAlign="center">
          <Text variant="h3">Let&apos;s make sure your name matches your ID</Text>
          <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
            We&apos;ll update it for you so your future licenses and work documents can be verified
            without delay.
          </Text>
          <Card variant="primary" sx={{ textAlign: "left" }}>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {items.map((item) => {
                return (
                  <Stack key={item.label} gap={1}>
                    <Text variant="caption" color={(theme) => theme.palette.text.primary}>
                      {item.label}
                    </Text>
                    <Text bold variant="body1" color={item.value.color}>
                      {item.value.text}
                    </Text>
                  </Stack>
                );
              })}
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
